<template>
<!-- WHAT: A components to collect histogram-metrics model data (Copied from BtDimsMetricsModel).
   HISTORY:
   * V260520.1: Changed validStep() rule to fix the error caused by decimal precision.
   * V260518.1: Fixed a bug that excluded DECIMAL(x,y) column types from histogramColumns.
   * V260515.1: Replaced BIGINT and DOUBLE type checks with NUMERIC_TYPES constant to support all numeric column types for histogram columns.
   * V260105.1: [not sent to RF] Removed hasFilter prop as it wasn't in use.
   * V251104.1: Fixed a 2-way binding issue by consuming the initial-model prop from BtMetricsModel.
   * V251030.1: Modified validLabel rule to prevent the label from being the same as the histogram column.
   * V251027.1: Supported 2-way binding: Initialized form fields from v-model.
   * V250807.1: Added hasFilter prop and passed it to bt-metrics-model.
   * V250710.1: 1st release.
-->
<v-form lazy-validation
   ref="form"
   v-model="isFormValid"
>
   <v-row class="px-2 pb-2">
      <v-col xs="12" sm="12" md="3" class="py-0"
      >
         <v-autocomplete persistent-hint required
            ref="on"
            autocomplete="off"
            class="py-0"
            placeholder="select a column..."
            hint="* Histogram Column"
            :items="histogramColumns"
            :rules="[rules.required]"
            v-model="on"
            @change="onChanged"
         ></v-autocomplete>
      </v-col>
      <v-col xs="12" sm="12" md="2" class="pt-2 pb-0">
         <v-text-field dense persistent-hint required hide-spin-buttons
            ref="min"
            class="py-0"
            placeholder="set the minimum range"
            hint="* Minimum Range"
            type="number"
            :rules="[rules.required]"
            v-model="min"
            @keyup="formChanged"
         ></v-text-field>
      </v-col>
      <v-col xs="12" sm="12" md="2" class="pt-2 pb-0">
         <v-text-field dense persistent-hint required hide-spin-buttons
            ref="max"
            class="py-0"
            placeholder="set the maximum range"
            hint="* Maximum Range"
            :disabled="!min"
            type="number"
            :rules="[rules.required, rules.validMax]"
            v-model="max"
            @keyup="formChanged"
         ></v-text-field>
      </v-col>
      <v-col xs="12" sm="12" md="2" class="pt-2 pb-0">
         <v-text-field dense persistent-hint required hide-spin-buttons
            ref="step"
            class="py-0"
            placeholder="set the step"
            hint="* Step"
            :disabled="!max"
            type="number"
            :rules="[rules.required, rules.validStep]"
            v-model="step"
            @keyup="formChanged"
         ></v-text-field>
      </v-col>
      <v-col xs="12" sm="12" md="3" class="pt-2 pb-0 pr-1">
         <v-text-field dense persistent-hint required hide-spin-buttons
            ref="label"
            class="py-0"
            placeholder="specify a label"
            hint="* Label"
            :disabled="!step"
            :rules="[rules.required, rules.validLabel]"
            v-model="label"
            @keyup="formChanged"
         ></v-text-field>
      </v-col>
   </v-row>
   <v-row>
      <v-col cols="12" class="pb-0">
         <bt-metrics-model has-filter
            :initial-model="initialModel"
            :schemas="schemas"
            :query-submitted="querySubmitted"
            v-model="metricsData"
            @form-valid="metricsDataChanged"
         />
      </v-col>
   </v-row>
</v-form>
</template>

<script>
import BtMetricsModel from './BtMetricsModel.vue';
import { isEmpty } from '../mixins/bt-mixin.js';

const NAME = "BtHistogramMetricsModel";
const VERSION = 'V260520.1';
// V260515
const NUMERIC_TYPES = ['TINYINT', 'SMALLINT', 'INTEGER', 'BIGINT', 'HUGEINT', 'DOUBLE', 'DECIMAL'];

export default {
   name: NAME,

   components: {
      BtMetricsModel
   },

   props: {
      value: {
         type: Object,
         default: () => ({})
      },
      debug: {
         type: Boolean,
         default: false
      },
      // hasFilter: {
      //    type: Boolean,
      //    default: false
      // },
      initialModel: {
         type: Boolean
      },
      schemas: {
         type: Array,
         required: true,
         default: () => []
      },
      querySubmitted: {
         type: Boolean,
         required: true
      }
   },

   data() {
      return {
         rules: {
            required: value => !!value || 'Value is required!',
            // validMin: () => !this.max || this.minNum < this.maxNum || `Value must be less than ${this.max}`,
            validMax: () => this.maxNum > this.minNum || `Value must be greater than ${this.min}`,
            //V260520: validStep: () => (this.maxNum - this.minNum) % this.stepNum === 0 || `Value must evenly divide the range! ${(this.maxNum - this.minNum) % this.stepNum}`,
            validStep: () => {
               const min = this.minNum;
               const max = this.maxNum;
               const step = this.stepNum;

               // Find how many decimal places the numbers have
               const getDecimals = (num) => (num.toString().split('.')[1] || '').length;
               const maxDecimals = Math.max(getDecimals(min), getDecimals(max), getDecimals(step));
               
               // Create a multiplier (e.g., 1 decimal place = 10, 2 = 100)
               const multiplier = Math.pow(10, maxDecimals);

               // Convert everything to integers for safe modulo math
               const range = Math.round((max - min) * multiplier);
               const scaledStep = Math.round(step * multiplier);

               return range % scaledStep === 0 || `Value must evenly divide the range!`;
            },
           // V251030
            validLabel: v => {
               if (!/^[^\d\s-][^\s-]*$/.test(v))
                  return 'Value must not start with a digit or contain spaces or dashes!';
               if (v.toLowerCase() === this.on.toLowerCase())
                  return 'Value cannot be the same as Histogram Column!';
               return true;
            }
         },
         isFormValid: false,
         on: '',
         min: '',
         max: '',
         step: '',
         label: '',
         metricsData: {},
         isMetricsModelValid: true
      }
   },

   computed: {
      minNum() {
         return Number(this.min) || 0;
      },
      maxNum() {
         return Number(this.max) || 0;
      },
      stepNum() {
         return Number(this.step) || 0;
      }
   },

   watch: {
      initialModel: {
         immediate: true,
         handler(val) {
            if (val) this.init();
         }
      },

      schemas: {
         immediate: true,
         deep: true,
         async handler(val) {
            // this.alert('in ' + NAME + '/watch: schemas=' + JSON.stringify(val));
            // this.on = '';
            // this.min = '';
            // this.max = '';
            // this.step = '';
            // this.label = '';
            // this.formChanged();
            if (val?.length) {
               //V260515: this.histogramColumns = val.filter(col => col.column_type === 'BIGINT' || col.column_type === 'DOUBLE')
               //V260518: this.histogramColumns = val.filter(col => NUMERIC_TYPES.includes(col.column_type))
               //    .map(col => col.column_name);
               this.histogramColumns = val.filter(col => NUMERIC_TYPES.some(type => col.column_type.startsWith(type)))
                  .map(col => col.column_name);
            } else {
               this.histogramColumns = [];
            }

            this.init();

            this.$nextTick(() => {
               this.$refs.on.focus();
            });
         }
      }
   },

   methods: {
      alert(msg) {
         if (this.debug) {
            alert(`${NAME} ${VERSION} says:\n${msg}`);
         }
      },

      log(msg, always =false) {
         if (this.debug || always) {
            console.log(`-----${NAME} ${VERSION} says => ${msg}`);
         }
      },

      init() {
         if (isEmpty(this.value) || !this.histogramColumns?.length) {
            this.on = '';
            this.min = '';
            this.max = '';
            this.step = '';
            this.label = '';
            this.metricsData = {};
            this.isMetricsModelValid = false;
         } else {
            const { histogramOn, histogramRange, histogramStep, histogramLable, metrics, whereClause } = this.value;

            this.on = this.histogramColumns.includes(histogramOn) ? histogramOn : '';
            this.min = histogramRange[0];
            this.max = histogramRange[1];
            this.step = histogramStep;
            this.label = histogramLable;            
            this.metricsData = {
               metrics: [...metrics],
               ...(whereClause && { whereClause: whereClause })
            }            
            this.isMetricsModelValid = true;
         }
      },

      onChanged() {
         this.formChanged();
         this.$nextTick(() => {
            this.$refs.min.focus();
         });
      },

      metricsDataChanged(val) {
         // alert('in metricsDataChanged(): val=' + val);
         this.isMetricsModelValid = val;
         this.formChanged();
      },

      formChanged() {
         let emitVal = false;
         this.isFormValid = this.$refs.form?.validate();
         // alert('in formChanged(): isFormValid=' + this.isFormValid);
         if (this.isFormValid && this.isMetricsModelValid) {
            const modelData = {
               histogramOn: this.on,
               histogramRange: [ +this.min, +this.max],
               histogramStep: +this.step,
               histogramLable: this.label,
               ...this.metricsData
            };

            this.$emit('input', modelData);
            emitVal = true;
         }

         this.$emit('form-valid', emitVal);
      }
   },

   mounted() {
      // alert(`in ${NAME}.mounted(): `);
      if (this.initialModel === undefined)
         this.init();
   }
}
</script>

<style scoped>
</style>